import cn from 'classnames'
import NextLink from 'next/link'
import {HTMLAttributeAnchorTarget, memo, ReactNode} from 'react'
import {EVENTS, track, TrackingProps} from 'src/common/helpers'
import {UrlObject} from 'url'

export type ButtonV2Variants = 'primary' | 'secondary' | 'tertiary' | 'icon-primary' | 'icon-secondary'
export type ButtonV2Sizes = 'small' | 'medium'

type ButtonProps = {
  children: ReactNode
  variant: ButtonV2Variants
  className?: string
  id?: string
  disabled?: boolean
  trackingData: Omit<TrackingProps, 'event'>
  size?: ButtonV2Sizes
  border?: boolean
} & {
  href?: string | UrlObject
  target?: HTMLAttributeAnchorTarget
  onClick?: VoidFunction
  scroll?: boolean
  type?: never
  locale?: string
}

export const ButtonV2 = memo(function Button({
  variant,
  children,
  className,
  onClick,
  trackingData,
  href,
  target,
  locale,
  id,
  type,
  disabled,
  size = 'medium',
  border = true,
}: ButtonProps) {
  const isIconButton = variant === 'icon-primary' || variant === 'icon-secondary'

  const generatedClassname = cn(
    'group relative isolate flex items-center justify-center text-nowrap rounded-[4px] text-black',
    {
      'bg-green-neon hover:bg-primary-button-hover': variant === 'primary' || variant === 'icon-primary',
      'bg-moss-white lg:hover:bg-moss-grey-100': variant === 'secondary' || variant === 'icon-secondary',
      'bg-moss-grey-100 hover:bg-moss-grey-100/[90%]': variant === 'tertiary',
      'max-md:h-[38px] h-12 text-button-s': size === 'medium',
      'h-8 lg:h-[38px] text-button-s': size === 'small',
      'px-4 py-3': !isIconButton,
    },
    className,
  )

  const handleClick = () => {
    track({
      event: EVENTS.CLICK,
      ...trackingData,
    })

    onClick?.()
  }

  if (href) {
    return (
      <NextLink
        className={generatedClassname}
        href={href}
        target={target}
        locale={locale}
        {...(id && {id})}
        onClick={handleClick}
      >
        <span className="z-10">{children}</span>
        <div
          className={cn('pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%]', {
            'group-hover:bg-moss-white/[20%] z-0': variant === 'primary' || variant === 'icon-primary',
          })}
        />
      </NextLink>
    )
  }

  return (
    <button
      className={generatedClassname}
      onClick={handleClick}
      type={type || 'button'}
      {...(id && {id})}
      disabled={disabled}
    >
      {children}
      {border && <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%]" />}
    </button>
  )
})
